import React, { useRef, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';

const CinematicCanvas = () => {
  const canvasRef = useRef(null);
  const { theme } = useTheme();

  const isDark = theme === 'dark';

  // High-end professional color palette
  const amberPrimary = '245, 158, 11';
  const amberSecondary = '217, 119, 6';

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', resize);
    resize();

    // Professional Particles System
    const particles = [];
    const baseCount = 240; // 20% more than previous 200
    const particleCount = Math.min(window.innerWidth / 6, baseCount); 
    const maxZ = 1600;
    
    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: (Math.random() - 0.5) * window.innerWidth * 2.8,
        y: (Math.random() - 0.5) * window.innerHeight * 2.8,
        z: Math.random() * maxZ,
        speed: (Math.random() * 0.35) + 0.15,
        size: Math.random() * 2.8 + 0.8,
        rotation: Math.random() * Math.PI * 2,
        spin: (Math.random() - 0.5) * 0.008,
        flicker: Math.random(),
        shimmer: Math.random() * 0.03,
      });
    }
    
    let scrollY = window.scrollY;
    let targetScrollY = scrollY;
    const handleScroll = () => {
      targetScrollY = window.scrollY;
    };
    window.addEventListener('scroll', handleScroll);
    
    let animationFrameId;
    let time = 0;
    
    const draw = () => {
      scrollY += (targetScrollY - scrollY) * 0.05;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const fov = 650;
      
      time += 0.01;
      
      const projected = [];
      
      particles.forEach((p, i) => {
        p.z -= p.speed + (scrollY * 0.002);
        p.rotation += p.spin;
        
        if (p.z < 1) {
          p.z = maxZ;
          p.x = (Math.random() - 0.5) * window.innerWidth * 3.2;
          p.y = (Math.random() - 0.5) * window.innerHeight * 3.2;
        }
        
        const scale = fov / p.z;
        const x2d = (p.x * scale) + centerX;
        const y2d = (p.y * scale) + centerY;
        
        if (x2d < -200 || x2d > canvas.width + 200 || y2d < -200 || y2d > canvas.height + 200) return;
        
        const opacity = Math.max(0, 1 - (p.z / maxZ));
        const flickerVal = (0.35 + Math.sin(time * 5 + i) * 0.65) * opacity;
        
        projected.push({ x: x2d, y: y2d, scale, opacity, flickerVal });
        
        // Draw High-End Particle Core
        ctx.beginPath();
        const r = p.size * scale;
        ctx.arc(x2d, y2d, Math.max(1.2, r), 0, Math.PI * 2);
        
        ctx.shadowBlur = scale * 8;
        ctx.shadowColor = `rgba(${amberPrimary}, ${flickerVal * 0.75})`;
        
        const gradient = ctx.createRadialGradient(x2d, y2d, 0, x2d, y2d, r);
        gradient.addColorStop(0, `rgba(255, 255, 255, ${flickerVal})`);
        gradient.addColorStop(0.2, `rgba(${amberPrimary}, ${flickerVal * 0.9})`);
        gradient.addColorStop(1, `rgba(${amberPrimary}, 0)`);
        
        ctx.fillStyle = gradient;
        ctx.fill();
        ctx.shadowBlur = 0;
      });
      
      // Elite Neural Connections
      ctx.lineWidth = 0.7;
      for (let i = 0; i < projected.length; i++) {
        if (i % 2 !== 0) continue; // Denser frequency
        
        for (let j = i + 1; j < projected.length; j++) {
          if (j % 5 !== 0) continue;

          const dx = projected[i].x - projected[j].x;
          const dy = projected[i].y - projected[j].y;
          const dist = dx * dx + dy * dy;
          
          const connectLimit = 25000 * Math.min(projected[i].scale, projected[j].scale); 
          
          if (dist < connectLimit) {
            const op = (1 - dist / connectLimit) * (projected[i].opacity * projected[j].opacity) * 0.45;
            
            ctx.beginPath();
            const grad = ctx.createLinearGradient(projected[i].x, projected[i].y, projected[j].x, projected[j].y);
            grad.addColorStop(0, `rgba(${amberPrimary}, ${op})`);
            grad.addColorStop(1, `rgba(${amberSecondary}, ${op * 0.2})`);
            
            ctx.strokeStyle = grad;
            ctx.moveTo(projected[i].x, projected[i].y);
            ctx.lineTo(projected[j].x, projected[j].y);
            ctx.stroke();
          }
        }
      }
      
      animationFrameId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(animationFrameId);
    };
  }, [theme, isDark]);

  return (
    <canvas
      ref={canvasRef}
      id="global-particles"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        zIndex: 1, // Layered in front of shader (-20) but behind content (10+)
      }}
    />
  );
};

export default CinematicCanvas;
